import { openInNewTab } from "@/utils/open-in-new-tab";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { cn } from "@/lib/utils";
import { popModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { TextChip } from "@/toggle-ui-kit/chip";
import IconButton from "@/toggle-ui-kit/icon-button";
import { X } from "lucide-react";
import { Fragment } from "react";

/**
 * 주석별 변경 분류 — dsd-dsd 대사 결과를 매핑한다.
 * - values_changed: 값 변경(구조 동일, 셀 값만)
 * - structure_changed: 행/열 변경(행·열 추가·삭제)
 * - unmatched: 주석 추가/삭제(한쪽에만 존재)
 * - no_change: 변경 없음(양쪽 동일)
 */
export type ChangeReviewCategory =
  | "values_changed"
  | "structure_changed"
  | "unmatched"
  | "no_change";

export interface ChangeReviewRow {
  tableName: string;
  category: ChangeReviewCategory;
  /** 새 주석이라 XBRL 태깅이 새로 필요한 표 */
  needsXbrlWork?: boolean;
}

interface ChangeReviewModalProps {
  rows: ChangeReviewRow[];
  /** dsd-dsd 대사에 넘길 직전본(src)·새 파일(trg)의 업로드 파일 id */
  srcFileId?: string;
  trgFileId?: string;
  /** 기본 동작(새 탭으로 대사 결과 열기)을 대체할 때만 전달 */
  onViewAll?: () => void;
  onTagNow?: () => void;
  onClose?: () => void;
}

const CATEGORY_META: Record<
  ChangeReviewCategory,
  { label: string; chipClassName: string; dotClassName: string }
> = {
  values_changed: {
    label: "값 변경",
    chipClassName: "bg-[#b9f8cf] text-[#00a63e]",
    dotClassName: "bg-[#05df72]",
  },
  structure_changed: {
    label: "행/열 변경",
    chipClassName: "bg-[#fef9c2] text-[#d08700]",
    dotClassName: "bg-[#fdc700]",
  },
  unmatched: {
    label: "주석 추가/삭제",
    chipClassName: "bg-[#ffe4e6] text-[#ff2056]",
    dotClassName: "bg-[#ff637e]",
  },
  no_change: {
    label: "변경 없음",
    chipClassName: "bg-[#f3f4f6] text-[#6a7282]",
    dotClassName: "bg-[#99a1af]",
  },
};

const CATEGORY_ORDER: ChangeReviewCategory[] = [
  "values_changed",
  "structure_changed",
  "unmatched",
];

export const ChangeReviewModal = ({
  rows,
  srcFileId,
  trgFileId,
  onViewAll,
  onTagNow,
  onClose = popModal,
}: ChangeReviewModalProps) => {
  // 요약 카운트도 목록도 실제 변경만 다룬다(변경 없음 제외).
  const changedRows = rows.filter((row) => row.category !== "no_change");
  const summary = CATEGORY_ORDER.map((category) => ({
    category,
    count: changedRows.filter((row) => row.category === category).length,
  }));
  const changedCount = summary.reduce((total, item) => total + item.count, 0);

  const canViewAll = Boolean(onViewAll || (srcFileId && trgFileId));
  const handleViewAll = () => {
    if (onViewAll) {
      onViewAll();
      return;
    }
    if (!srcFileId || !trgFileId) return;
    openInNewTab(`/compare/dsd-dsd/result?src=${srcFileId}&trg=${trgFileId}`);
  };

  return (
    <ModalWrapper className="flex w-[600px] max-w-[calc(100vw-48px)] flex-col gap-0 overflow-hidden rounded-[8px] border-line-normal bg-white p-0 shadow-xxlarge">
      <div className="flex items-center justify-between px-6 pt-5">
        <h2 className="headline-2-semibold text-label-normal">
          변경 사항 확인
        </h2>
        <IconButton
          variant="transparent"
          icon={<X className="size-4" />}
          onClick={onClose}
          aria-label="닫기"
        />
      </div>

      <div className="flex flex-col gap-3 px-6 pb-6 pt-4">
        <div className="flex flex-col gap-3 rounded-[8px] bg-background-neutral-alternative p-4">
          <p className="body-2-normal-medium text-label-normal">
            변경 사항 <span className="font-semibold">{changedCount}건</span>이
            확인되었습니다
          </p>
          <div className="flex items-center gap-3 rounded-[12px] bg-white p-3">
            {summary.map(({ category, count }, index) => {
              const meta = CATEGORY_META[category];
              return (
                <Fragment key={category}>
                  {index > 0 && (
                    <div className="w-px self-stretch bg-line-neutral" />
                  )}
                  <div className="flex min-w-0 flex-1 items-center">
                    <span className="flex-1 caption-1-medium text-label-normal">
                      {meta.label}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span
                        className={cn(
                          "size-2.5 rounded-full",
                          meta.dotClassName,
                        )}
                      />
                      <span className="body-1-normal-semibold text-label-normal">
                        {count}
                      </span>
                    </span>
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <p className="label-2-regular text-label-normal">주석 변경 사항</p>
          <div className="flex flex-col overflow-hidden rounded-[8px] border border-line-normal">
            <div className="flex shrink-0 gap-2 bg-line-alternative px-4 py-1.5">
              <span className="w-[100px] shrink-0 caption-1-medium text-label-alternative">
                변경 사항
              </span>
              <span className="flex-1 caption-1-medium text-label-alternative">
                주석
              </span>
            </div>
            <div className="flex max-h-[228px] min-h-0 flex-col overflow-y-auto">
              {changedRows.length === 0 && (
                <p className="px-4 py-6 text-center body-2-normal-medium text-label-alternative">
                  변경된 주석이 없습니다
                </p>
              )}
              {changedRows.map((row, index) => {
                const meta = CATEGORY_META[row.category];
                return (
                  <div
                    key={`${row.tableName}-${index}`}
                    className="flex items-center gap-2 px-4 py-2"
                  >
                    <div className="flex w-[100px] shrink-0 items-center">
                      <TextChip size="large" className={meta.chipClassName}>
                        {meta.label}
                      </TextChip>
                    </div>
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span
                        className="truncate body-2-normal-medium text-label-normal"
                        title={row.tableName}
                      >
                        {row.tableName}
                      </span>
                      {row.needsXbrlWork && (
                        <TextChip
                          size="small"
                          className="shrink-0 bg-blue-500 text-static-white"
                        >
                          XBRL 작업 필요
                        </TextChip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 bg-white px-6 pb-5">
        <Button
          variant="outlined"
          color="secondary"
          size="base"
          disabled={!canViewAll}
          onClick={handleViewAll}
        >
          변경 내역 전체 보기
        </Button>
        <Button
          variant="solid"
          color="primary"
          size="base"
          onClick={onTagNow ?? onClose}
        >
          바로 태깅하기
        </Button>
      </div>
    </ModalWrapper>
  );
};
